import { User } from "firebase/auth";
import { db } from "./firebase";
import { notify } from "../miscellaneous/reatToastify/ToastNotification";
import {
  doc,
  getDoc,
  setDoc,
  getDocs,
  addDoc,
  collection,
  query,
  where,
  updateDoc,
  deleteDoc,
  DocumentData,
  QuerySnapshot,
  DocumentSnapshot,
} from "firebase/firestore";

/*********************************************************
 * Firebase module  with the closure tecnique (encapsulation)
 * Exported to Login.js
 *********************************************************/
export default function FirebaseModule() {
  ///////////////////////////////////////////////////////////////
  // Encapsulation tecnique (Closures) to use in conjunction with
  // the public functions starting in line 83
  // https://firebase.google.com/docs/firestore/query-data/get-data
  function checkDataFromDatabase(
    pathCollect: string,
    documentId: string,
  ): Promise<DocumentSnapshot | void> {
    const ref = doc(db, `/${pathCollect}/${documentId}`);
    return getDoc(ref);
  }
  // https://firebase.google.com/docs/firestore/manage-data/add-data
  function addDataToDatabase(
    pathCollect: string,
    documentId: string,
    data: any,
  ): Promise<void> {
    // documentID is the document (reference or name) in firebase
    return setDoc(doc(db, pathCollect, documentId), data);
  }
  // ref: https://firebase.google.com/docs/firestore/query-data/queries#simple_queries
  // checks validation from the already data in the database from the id object
  // keyInDatabase works like a hashmap or dictionary, the key will return the value id: 'value'
  function getDocFromDatabase(
    pathCollect: string,
    field: string,
    value: string,
  ): Promise<DocumentData> {
    const docRef = collection(db, pathCollect);
    const q = query(docRef, where(field, "==", value));
    return getDocs(q); // returns a promise.then(() => {})... it needs to be iterated again
  }

  /****************************************************
   * gets all the documents based on the condition
   * const docsData = queryshotQuery.docs.map(doc => ({
   * id: doc.id,
   * ...doc.data()}))
   * setData(docsData);
   ***************************************************/

  //https://firebase.google.com/docs/firestore/query-data/get-data#get_all_documents_in_a_collection
  function getAllDocsFromDatabase(
    pathCollect: string,
  ): Promise<QuerySnapshot<DocumentData>> {
    return getDocs(collection(db, pathCollect));
  }

  // https://firebase.google.com/docs/firestore/manage-data/add-data#update-data
  function updateDocToDatabase(
    pathCollect: string,
    documentId: string,
    data: any,
  ): Promise<void> {
    const documentRef = doc(db, pathCollect, documentId);
    return updateDoc(documentRef, data);
  }
  // creates a unique document id in firebase with addDoc
  // https://firebase.google.com/docs/firestore/manage-data/add-data
  // updates the document with the autogenerated id
  async function addDocWithIDToDatabase(
    pathCollect: string,
    data: any,
  ): Promise<void> {
    try {
      const ref = await addDoc(collection(db, pathCollect), data);
      //   const newRef = await setDoc(ref, dataObj);
      const idref = ref.id;

      // Make sure this is awaited so errors propagate correctly
      await updateDocToDatabase(pathCollect, idref, { id: idref });
    } catch (error) {
      console.error("Error adding document:", error);
      notify("Error adding document", "error");
      throw error; // rethrow so the caller knows
    }
  }

  // https://firebase.google.com/docs/firestore/manage-data/delete-data
  function deleteDocFromDatabase(
    pathCollect: string,
    documentId: string,
  ): Promise<void> {
    return deleteDoc(doc(db, pathCollect, documentId));
  }

  // https://firebase.google.com/docs/firestore/manage-data/transactions#batched-writes
  // deletes a group of documents based on the conditions after certain amount of time

  ///////////////////////////////////////////////////////////////
  // will invoke an instance of the closure module,
  function checkDataInFirebase(pathCollect: string, documentId: string) {
    return checkDataFromDatabase(pathCollect, documentId);
  }

  function addDataInFirebase(
    pathCollect: string,
    documentId: string,
    data: any,
  ) {
    return addDataToDatabase(pathCollect, documentId, data);
  }

  function getDocInFirebase(pathCollect: string, field: string, value: string) {
    return getDocFromDatabase(pathCollect, field, value);
  }

  function getAllDocsFromFirebase(pathCollect: string) {
    return getAllDocsFromDatabase(pathCollect);
  }

  function addDocumentWithFirebaseID(pathCollect: string, data: any) {
    return addDocWithIDToDatabase(pathCollect, data);
  }

  function updateDocToFirebase(
    pathCollect: string,
    documentId: string,
    data: any,
  ) {
    return updateDocToDatabase(pathCollect, documentId, data);
  }
  function deleteDocFromFirebase(pathCollect: string, documentId: string) {
    return deleteDocFromDatabase(pathCollect, documentId);
  }

  return {
    checkDataInFirebase,
    addDataInFirebase,
    getDocInFirebase,
    getAllDocsFromFirebase,
    addDocumentWithFirebaseID,
    updateDocToFirebase,
    deleteDocFromFirebase,
  };
}
